/**
 * RENTAL MARKETPLACE — buyer discovery page.
 *
 * Wired to `GET /api/rentals/nearby` and `POST /api/rentals/reserve`.
 * Falls back to a small demo list if the API returns nothing so the
 * page still renders cleanly during empty-state.
 *
 * Routes used:
 *   GET  /api/rentals/nearby?city=&category=&limit=
 *   GET  /api/rentals/store/{store_id}?start_date=&end_date=
 *   POST /api/rentals/reserve
 */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Calendar, MapPin, Star, ShoppingBag, Package, Filter, Heart,
         Sparkles, ChevronRight, X, Check, Search, Plus, Minus } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useAuth } from "../lib/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import VoiceMicButton from "../components/VoiceMicButton";
import ColorSwatchPicker from "../components/ColorSwatchPicker";


// ── MOCK_STORES kept as design reference only; the live page fetches
//    from /api/rentals/nearby. Empty array so dead-code elimination
//    keeps the bundle small.
const MOCK_STORES = [];


// ── DATE PICKER (sticky, top of page) ───────────────────────────────
function DateRangePicker({ start, end, days, onChange }) {
  const today = new Date().toISOString().slice(0, 10);
  const inFiveDays = new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);
  return (
    <div className="card p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end"
      data-testid="rental-date-picker">
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">
          <Calendar size={10} className="inline mr-1"/>Pickup
        </div>
        <input type="date" min={today} value={start || today}
          data-testid="rental-date-start"
          onChange={(e) => onChange(e.target.value, end)}
          className="input-dark text-sm w-full"/>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">
          <Calendar size={10} className="inline mr-1"/>Return
        </div>
        <input type="date" min={start || today} value={end || inFiveDays}
          data-testid="rental-date-end"
          onChange={(e) => onChange(start, e.target.value)}
          className="input-dark text-sm w-full"/>
      </label>
      <div className="bg-[#D4FF00]/10 border border-[#D4FF00]/30 rounded-xl px-4 py-2 text-center">
        <div className="text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold">Duration</div>
        <div className="font-display font-black text-2xl text-[#D4FF00]">{days}</div>
        <div className="text-[9px] uppercase text-[#A1A1AA]">{days === 1 ? "day" : "days"}</div>
      </div>
    </div>
  );
}


// ── STORE CARD ──────────────────────────────────────────────────────
function RentalStoreCard({ store, onOpen }) {
  return (
    <button onClick={() => onOpen(store)}
      data-testid={`rental-store-${store.store_id}`}
      className="card overflow-hidden hover:border-[#D4FF00] transition text-left w-full group">
      <div className="relative h-44 overflow-hidden">
        <img src={store.cover}
          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
          alt={store.name}/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0F1F] via-transparent to-transparent"/>
        <div className="absolute top-3 left-3 flex gap-1">
          <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded bg-[#1A0F1F]/80 backdrop-blur text-[#D4FF00]">
            {store.category === "jewellery" ? "💎 Jewellery" : "👗 Clothes"}
          </span>
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded bg-[#1A0F1F]/80 backdrop-blur">
          <MapPin size={10} className="text-[#00F0FF]"/>
          <span className="text-[10px] font-bold uppercase tracking-widest">
            {store.distance_km ? `${store.distance_km} km` : store.city || "—"}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-lg uppercase truncate">{store.name}</div>
            <div className="text-[10px] text-[#A1A1AA] truncate">{store.owner_name} · {store.city}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-center gap-1 text-[#D4FF00]">
              <Star size={12} fill="#D4FF00"/>
              <span className="font-bold text-sm">{store.rating}</span>
            </div>
            <div className="text-[9px] text-[#A1A1AA]">{store.review_count} reviews</div>
          </div>
        </div>
        <div className="flex gap-1 mb-3">
          {store.items.slice(0, 4).map(p => (
            <img key={p.id} src={p.image}
              className="w-12 h-12 rounded-lg object-cover border border-[#3D2845]"
              alt={p.name}/>
          ))}
          {store.items.length > 4 && (
            <div className="w-12 h-12 rounded-lg bg-[#3D2845] flex items-center justify-center text-[9px] font-bold text-[#A1A1AA]">
              +{store.items.length - 4}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between text-xs">
          <div>
            <span className="text-[#A1A1AA]">From </span>
            <span className="font-display font-black text-[#D4FF00] text-lg">{fmtMoney(store.starts_at, { decimals: 0 })}</span>
            <span className="text-[#A1A1AA]">/day</span>
          </div>
          <div className="text-[#A1A1AA]">
            Deposit from <span className="text-[#FAFAFA] font-bold">{fmtMoney(store.deposit_from, { decimals: 0 })}</span>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#D4FF00] group-hover:text-[#FAFAFA]">
          View collection <ChevronRight size={11}/>
        </div>
      </div>
    </button>
  );
}


// ── STORE DRAWER (multi-select + cart) — wired to API ────────────
function StoreDrawer({ store: initialStore, startDate, endDate, days, onClose,
                       onReserved, onAddedToCart, savedIds, onToggleSave }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [picked, setPicked] = useState({});   // { product_id: { size?: string } }
  const [items, setItems] = useState(initialStore.items || []);
  const [busy, setBusy] = useState(false);
  const [cartBusy, setCartBusy] = useState(false);
  const [err, setErr] = useState("");

  // Re-pull the store with date-aware availability map + size + description.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/rentals/store/${initialStore.store_id}`, {
          params: { start_date: startDate, end_date: endDate },
        });
        if (alive && data?.products) {
          setItems(data.products.map(p => ({
            id: p.product_id, name: p.name, image: p.image,
            description: p.description || "",
            sizes: p.sizes || [],
            colors: p.colors || [],
            category_id: p.category_id,
            price_per_day: p.price_per_day, deposit: p.deposit,
            available: p.available, available_qty: p.available_qty ?? 1,
            total_stock: p.total_stock ?? 1,
            min_days: p.min_days, max_days: p.max_days,
          })));
        }
      } catch { /* fall back to initial mock */ }
    })();
    return () => { alive = false; };
  }, [initialStore.store_id, startDate, endDate]);

  const toggle = (id) => {
    setPicked(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = { size: "", color: "" };
      return next;
    });
    setErr("");
  };
  const setSize = (id, size) => setPicked(prev => prev[id] ? { ...prev, [id]: { ...prev[id], size } } : prev);
  const setColor = (id, color) => setPicked(prev => prev[id] ? { ...prev, [id]: { ...prev[id], color } } : prev);

  const pickedIds = Object.keys(picked);
  const pickedItems = items.filter(i => pickedIds.includes(i.id));
  const dailyTotal = pickedItems.reduce((s, i) => s + i.price_per_day, 0);
  const rentalTotal = dailyTotal * days;
  const depositTotal = pickedItems.reduce((s, i) => s + i.deposit, 0);
  const grandTotal = rentalTotal + depositTotal;

  // Validation — products that need a size but the buyer hasn't picked one.
  const needsSize = pickedItems.filter(p => p.sizes.length > 0 && !picked[p.id]?.size);
  const needsColor = pickedItems.filter(p => (p.colors || []).length > 0 && !picked[p.id]?.color);

  const reserve = async () => {
    if (!user) { navigate("/login?redirect=/rentals"); return; }
    if (pickedIds.length === 0) return;
    if (needsSize.length) {
      setErr(`Please pick a size for: ${needsSize.map(p => p.name).join(", ")}`);
      return;
    }
    if (needsColor.length) {
      setErr(`Please pick a colour for: ${needsColor.map(p => p.name).join(", ")}`);
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post("/rentals/reserve", {
        store_id: initialStore.store_id,
        product_ids: pickedIds,
        start_date: startDate,
        end_date: endDate,
      });
      onReserved?.();
      onClose();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.conflicting_product_ids?.length) {
        setErr(`Date conflict — ${data.conflicting_product_ids.length} item(s) already booked for these dates. Please pick a different window.`);
      } else {
        const d = data?.detail;
        setErr(typeof d === "string" ? d : (d?.detail || "Reservation failed"));
      }
    } finally { setBusy(false); }
  };

  // Stage the selected items into the multi-seller Rental Cart. Same
  // dates apply to all items in this batch (the buyer picked them at
  // the page level). They can mix sellers from other drawers later.
  const addToCart = async () => {
    if (!user) { navigate("/login?redirect=/rentals"); return; }
    if (pickedIds.length === 0) return;
    if (needsSize.length) {
      setErr(`Please pick a size for: ${needsSize.map(p => p.name).join(", ")}`);
      return;
    }
    if (needsColor.length) {
      setErr(`Please pick a colour for: ${needsColor.map(p => p.name).join(", ")}`);
      return;
    }
    setCartBusy(true); setErr("");
    try {
      for (const id of pickedIds) {
        await api.post("/rentals/cart/add", {
          product_id: id,
          start_date: startDate,
          end_date: endDate,
          size: picked[id]?.size || "",
          color: picked[id]?.color || "",
          qty: 1,
        });
      }
      onAddedToCart?.(pickedIds.length);
      onClose();
    } catch (e) {
      setErr("Couldn't add to cart. Try again.");
    } finally { setCartBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4"
      data-testid="rental-store-drawer" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#1A0F1F] border border-[#3D2845] rounded-t-2xl md:rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-[#1A0F1F] border-b border-[#3D2845] px-5 py-4 flex items-center gap-3">
          <img src={initialStore.cover} className="w-12 h-12 rounded-lg object-cover" alt=""/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-xl uppercase truncate">{initialStore.name}</div>
            <div className="text-[10px] text-[#A1A1AA] flex items-center gap-2">
              <MapPin size={9}/>{initialStore.city} · {initialStore.distance_km ?? "—"} km
              <span>· </span><Star size={9} className="text-[#D4FF00]"/>{initialStore.rating}
            </div>
          </div>
          <button onClick={onClose}
            data-testid="rental-drawer-close"
            className="w-8 h-8 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
            <X size={14}/>
          </button>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px]">
          {/* Products grid — multi-select */}
          <div className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-3">
              Tap to add — multi-select supported · pick a size if shown
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {items.map(p => {
                const on = !!picked[p.id];
                const oos = p.available === false || p.available_qty === 0;
                const saved = savedIds?.has(p.id);
                return (
                  <div key={p.id}
                    data-testid={`rental-item-${p.id}`}
                    className={`relative card overflow-hidden text-left transition ${
                      on ? "ring-2 ring-[#D4FF00] border-[#D4FF00]" :
                      oos ? "opacity-60" :
                      "hover:border-[#3D2845]"
                    }`}>
                    <button onClick={() => !oos && toggle(p.id)}
                      disabled={oos} className="w-full text-left">
                      <div className="relative h-32 overflow-hidden">
                        <img src={p.image} className="w-full h-full object-cover" alt={p.name}/>
                        {on && (
                          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#D4FF00] text-black flex items-center justify-center font-bold shadow-lg">
                            <Check size={14}/>
                          </div>
                        )}
                        {/* Stock pill — bottom-left */}
                        <div className="absolute bottom-2 left-2">
                          {oos ? (
                            <span className="text-[9px] uppercase tracking-widest font-bold text-[#FF007F] bg-[#1A0F1F]/90 px-2 py-0.5 rounded"
                              data-testid={`rental-item-stock-${p.id}`}>
                              Out of stock
                            </span>
                          ) : p.available_qty <= 3 ? (
                            <span className="text-[9px] uppercase tracking-widest font-bold text-[#FF007F] bg-[#1A0F1F]/90 px-2 py-0.5 rounded"
                              data-testid={`rental-item-stock-${p.id}`}>
                              Only {p.available_qty} left
                            </span>
                          ) : (
                            <span className="text-[9px] uppercase tracking-widest font-bold text-[#D4FF00] bg-[#1A0F1F]/90 px-2 py-0.5 rounded"
                              data-testid={`rental-item-stock-${p.id}`}>
                              {p.available_qty} available
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* Heart — save / unsave */}
                    {onToggleSave && (
                      <button onClick={(e) => { e.stopPropagation(); onToggleSave(p.id); }}
                        data-testid={`rental-item-save-${p.id}`}
                        className={`absolute top-2 left-2 w-7 h-7 rounded-full backdrop-blur flex items-center justify-center transition ${
                          saved ? "bg-[#FF007F] text-white" : "bg-[#1A0F1F]/70 text-white hover:bg-[#FF007F]"
                        }`}>
                        <Heart size={13} className={saved ? "fill-white" : ""}/>
                      </button>
                    )}
                    <div className="p-2">
                      <div className="font-bold text-xs line-clamp-1">{p.name}</div>
                      {p.description && (
                        <div className="text-[9px] text-[#A1A1AA] line-clamp-2 mt-0.5"
                          data-testid={`rental-item-desc-${p.id}`}>
                          {p.description}
                        </div>
                      )}
                      <div className="text-[10px] text-[#A1A1AA] mt-1">
                        <span className="text-[#D4FF00] font-bold">{fmtMoney(p.price_per_day)}</span>/day
                        <span className="mx-1">·</span>
                        Dep {fmtMoney(p.deposit, { decimals: 0 })}
                      </div>
                      {/* Size selector — only when seller has set sizes
                          and this item is currently picked. */}
                      {on && p.sizes.length > 0 && (
                        <select value={picked[p.id]?.size || ""}
                          onChange={(e) => setSize(p.id, e.target.value)}
                          data-testid={`rental-item-size-${p.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="input-dark text-[11px] mt-2 w-full">
                          <option value="">Pick size *</option>
                          {p.sizes.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      )}
                      {/* Colour swatches — visible whenever this item
                          is selected AND the seller defined colours. */}
                      {on && (p.colors || []).length > 0 && (
                        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                          <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">
                            Pick colour *
                          </div>
                          <ColorSwatchPicker
                            colors={p.colors}
                            value={picked[p.id]?.color || ""}
                            onChange={(name) => setColor(p.id, name)}
                            testidPrefix={`rental-item-color-${p.id}`}
                            size="sm"/>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cart panel */}
          <div className="lg:border-l border-[#3D2845] bg-[#0F0817] p-4 sm:p-5 lg:sticky lg:top-[88px] lg:max-h-[calc(92vh-88px)] lg:overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingBag size={14} className="text-[#D4FF00]"/>
              <span className="font-display font-bold uppercase text-sm">Your reservation</span>
            </div>
            {pickedIds.length === 0 ? (
              <div className="text-center py-10 text-[#A1A1AA] text-xs">
                <Package size={28} className="mx-auto opacity-40 mb-2"/>
                Pick items from the left to start a rental.
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {pickedItems.map(p => (
                    <div key={p.id} className="flex items-center gap-2"
                      data-testid={`rental-cart-${p.id}`}>
                      <img src={p.image} className="w-10 h-10 rounded object-cover" alt=""/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold truncate">{p.name}</div>
                        <div className="text-[10px] text-[#A1A1AA]">
                          {fmtMoney(p.price_per_day)}/day · {fmtMoney(p.deposit, { decimals: 0 })} dep
                          {picked[p.id]?.size && <> · <span className="text-[#D4FF00]">{picked[p.id].size}</span></>}
                          {picked[p.id]?.color && (
                            <> · <span className="inline-flex items-center gap-1">
                              <span className="w-2.5 h-2.5 rounded-full border border-[#3D2845] inline-block"
                                style={{ background: (p.colors || []).find(c => c.name === picked[p.id].color)?.hex || "#888" }}/>
                              <span className="text-[#FAFAFA]">{picked[p.id].color}</span>
                            </span></>
                          )}
                        </div>
                      </div>
                      <button onClick={() => toggle(p.id)}
                        className="text-[#FF007F] text-xs">×</button>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#3D2845] pt-3 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Daily rate</span><span className="font-bold">{fmtMoney(dailyTotal)}/day</span></div>
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">× {days} days</span><span className="font-bold">{fmtMoney(rentalTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-[#A1A1AA]">Refundable deposit</span><span className="font-bold text-[#00F0FF]">+{fmtMoney(depositTotal)}</span></div>
                  <div className="border-t border-[#3D2845] mt-2 pt-2 flex justify-between text-base">
                    <span className="font-bold">Total today</span>
                    <span className="font-display font-black text-[#D4FF00] text-xl">{fmtMoney(grandTotal)}</span>
                  </div>
                  <div className="text-[9px] text-[#A1A1AA] text-right">Deposit refunded on safe return</div>
                </div>
                {err && (
                  <div className="mt-3 text-[10px] text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 rounded p-2"
                    data-testid="rental-reserve-error">{err}</div>
                )}
                {/* Dual CTA — quick reserve (single store) OR stage for
                    a multi-seller cart checkout. */}
                <button className="btn-secondary w-full mt-4 inline-flex items-center justify-center gap-1"
                  onClick={addToCart} disabled={cartBusy || busy}
                  data-testid="rental-add-to-cart-btn">
                  <ShoppingBag size={13}/>
                  {cartBusy ? "Adding…" : `Add ${pickedIds.length} to cart`}
                </button>
                <button className="btn-primary w-full mt-2"
                  onClick={reserve} disabled={busy || cartBusy}
                  data-testid="rental-reserve-btn">
                  {busy ? "Reserving…" : `Reserve ${pickedIds.length} item${pickedIds.length !== 1 ? "s" : ""} now`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── ADVANCED FILTERS PANEL ──────────────────────────────────────────
function AdvancedFilters({ filters, onChange, rentalCats, onReset }) {
  const set = (k, v) => onChange({ ...filters, [k]: v });
  return (
    <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      data-testid="rental-advanced-filters">
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">
          <Search size={10} className="inline mr-1"/>Search
        </div>
        <div className="flex gap-1.5 items-center">
          <input value={filters.q} onChange={e => set("q", e.target.value)}
            data-testid="rental-filter-q"
            placeholder="Lehenga, kurta, sneakers…"
            className="input-dark text-xs w-full"/>
          <VoiceMicButton
            testid="rental-filter-q-voice"
            onTranscript={(t) => set("q", t)}
            size={12} className="w-8 h-8"/>
        </div>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Category</div>
        <select value={filters.category_id} onChange={e => set("category_id", e.target.value)}
          data-testid="rental-filter-category" className="input-dark text-xs w-full">
          <option value="">All rental categories</option>
          {rentalCats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Max USD/day</div>
        <input type="number" min="0" value={filters.max_price}
          data-testid="rental-filter-max-price"
          onChange={e => set("max_price", e.target.value)}
          placeholder="∞" className="input-dark text-xs w-full"/>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Max deposit (USD)</div>
        <input type="number" min="0" value={filters.max_deposit}
          data-testid="rental-filter-max-deposit"
          onChange={e => set("max_deposit", e.target.value)}
          placeholder="∞" className="input-dark text-xs w-full"/>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Min rating</div>
        <select value={filters.min_rating}
          data-testid="rental-filter-min-rating"
          onChange={e => set("min_rating", e.target.value)} className="input-dark text-xs w-full">
          <option value="">Any</option>
          <option value="4.5">4.5+ ★★★★★</option>
          <option value="4">4.0+ ★★★★</option>
          <option value="3">3.0+ ★★★</option>
        </select>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Size</div>
        <select value={filters.size} onChange={e => set("size", e.target.value)}
          data-testid="rental-filter-size" className="input-dark text-xs w-full">
          <option value="">Any size</option>
          {["XS", "S", "M", "L", "XL", "XXL", "Free Size"].map(s =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">City</div>
        <input value={filters.city} onChange={e => set("city", e.target.value)}
          data-testid="rental-filter-city"
          placeholder="Mumbai, NYC…" className="input-dark text-xs w-full"/>
      </label>
      <label className="block">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-1">Sort</div>
        <select value={filters.sort} onChange={e => set("sort", e.target.value)}
          data-testid="rental-filter-sort" className="input-dark text-xs w-full">
          <option value="">Rating (default)</option>
          <option value="price_asc">$/day · low → high</option>
          <option value="price_desc">$/day · high → low</option>
          <option value="deposit_asc">Deposit · low → high</option>
          <option value="newest">Newest</option>
        </select>
      </label>
      <button onClick={onReset} className="btn-secondary text-[10px] uppercase tracking-widest"
        data-testid="rental-filter-reset">
        <X size={11} className="inline mr-1"/> Reset filters
      </button>
    </div>
  );
}


// ── MAIN PAGE ───────────────────────────────────────────────────────
export default function RentalsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const inFiveDays = new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);
  const { user } = useAuth();
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(inFiveDays);
  const [drawerStore, setDrawerStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformOn, setPlatformOn] = useState(true);
  const [toast, setToast] = useState("");
  const [view, setView] = useState("stores"); // 'stores' | 'products' (search results)
  const [searchResults, setSearchResults] = useState([]);
  const [rentalCats, setRentalCats] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  // Save list (♡) — set of product_ids we already saved
  const [savedIds, setSavedIds] = useState(new Set());

  const [filters, setFilters] = useState({
    q: "", category_id: "", max_price: "", max_deposit: "",
    min_rating: "", size: "", city: "", sort: "",
  });
  // Honor `?category_id=cat_xxx` deep-links from CategoryPage's "Rent
  // the look" rail so the rental browse opens already filtered.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("category_id");
    if (cid) setFilters(f => ({ ...f, category_id: cid }));
  }, [searchParams]);
  const filterIsActive = filters.q || filters.category_id || filters.max_price ||
    filters.max_deposit || filters.min_rating || filters.size || filters.city ||
    filters.sort;

  // Load rental-eligible categories once
  useEffect(() => {
    api.get("/rentals/categories").then(({ data }) => setRentalCats(data || []))
      .catch(() => setRentalCats([]));
  }, []);

  // Load saved items + cart count for the logged-in buyer
  const refreshSaved = useCallback(async () => {
    if (!user) { setSavedIds(new Set()); return; }
    try {
      const { data } = await api.get("/me/saved/product");
      const ids = data?.ids || (data?.items || []).map(p => p.product_id);
      setSavedIds(new Set(ids));
    } catch { setSavedIds(new Set()); }
  }, [user]);

  const refreshCart = useCallback(async () => {
    if (!user) { setCartCount(0); return; }
    try {
      const { data } = await api.get("/rentals/cart");
      setCartCount(data?.item_count || 0);
    } catch { setCartCount(0); }
  }, [user]);

  useEffect(() => { refreshSaved(); refreshCart(); }, [refreshSaved, refreshCart]);

  // Toggle save (♡) — single endpoint covers add+remove via toggle.
  const toggleSave = useCallback(async (productId) => {
    if (!user) { setToast("Sign in to save"); setTimeout(() => setToast(""), 2500); return; }
    try {
      await api.post(`/me/save/product/${productId}`);
      setSavedIds(prev => {
        const next = new Set(prev);
        if (next.has(productId)) next.delete(productId); else next.add(productId);
        return next;
      });
    } catch {
      setToast("Save failed"); setTimeout(() => setToast(""), 2000);
    }
  }, [user]);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 24 };
      if (filters.city) params.city = filters.city;
      if (filters.category_id) {
        // Re-use category-id directly (server may interpret as eligible)
        params.category = filters.category_id === "cat_jewelry" ? "Jewelry & Accessories"
          : filters.category_id === "cat_fashion" ? "Fashion & Apparel" : undefined;
      }
      const { data } = await api.get("/rentals/nearby", { params });
      setPlatformOn(data.platform_enabled !== false);
      const list = (data.stores || []).map(s => ({
        ...s,
        items: (s.items || []).map(it => ({
          ...it,
          id: it.product_id || it.id,
        })),
      }));
      setStores(list);
    } catch (e) {
      setStores([]);
    } finally { setLoading(false); }
  }, [filters.city, filters.category_id]);

  // Search products directly when ANY advanced filter is in use.
  const fetchSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (filters.q) params.q = filters.q;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.max_price) params.max_price = filters.max_price;
      if (filters.max_deposit) params.max_deposit = filters.max_deposit;
      if (filters.min_rating) params.min_rating = filters.min_rating;
      if (filters.size) params.size = filters.size;
      if (filters.city) params.city = filters.city;
      if (filters.sort) params.sort = filters.sort;
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const { data } = await api.get("/rentals/search", { params });
      setSearchResults(data.items || []);
      setPlatformOn(true);
    } catch (e) {
      setSearchResults([]);
    } finally { setLoading(false); }
  }, [filters, start, end]);

  // Pick the right fetcher based on whether the user is using filters.
  useEffect(() => {
    if (filterIsActive) { setView("products"); fetchSearch(); }
    else { setView("stores"); fetchStores(); }
  }, [filterIsActive, fetchSearch, fetchStores]);

  const days = useMemo(() => {
    if (!start || !end) return 1;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    return Math.max(1, Math.ceil(ms / 86400000) + 1);
  }, [start, end]);

  // Quick-add a single search-result product to the rental cart.
  const quickAddToCart = async (item) => {
    if (!user) { setToast("Sign in to add to cart"); setTimeout(() => setToast(""), 2500); return; }
    try {
      await api.post("/rentals/cart/add", {
        product_id: item.product_id,
        start_date: start, end_date: end,
        size: item.sizes && item.sizes.length === 1 ? item.sizes[0] : "",
        qty: 1,
      });
      setToast(`Added "${item.name}" to rental cart`);
      setTimeout(() => setToast(""), 2500);
      refreshCart();
    } catch {
      setToast("Couldn't add. Try again.");
      setTimeout(() => setToast(""), 2000);
    }
  };

  if (!platformOn) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="rentals-page">
        <div className="text-center max-w-md">
          <Package size={48} className="mx-auto text-[#A1A1AA] mb-3 opacity-50"/>
          <h2 className="font-display font-black text-2xl uppercase mb-2">Rentals paused</h2>
          <p className="text-sm text-[#A1A1AA]">The rental marketplace is temporarily disabled. Please check back soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 pb-24" data-testid="rentals-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#D4FF00] font-bold mb-2">
              <Sparkles size={11}/> NEW ON SHOPRIZZ
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl uppercase leading-none mb-2">
              Rent designer<br/>
              <span className="text-[#D4FF00]">looks &amp; jewels</span><br/>
              <span className="text-[#A1A1AA] text-3xl sm:text-4xl">near you.</span>
            </h1>
            <p className="text-sm text-[#A1A1AA] max-w-xl">
              Pick your dates, browse nearby ateliers, and reserve designer outfits and statement jewellery — pay per day plus a refundable deposit.
            </p>
          </div>
          {/* Cart pill — top right */}
          {user && (
            <Link to="/rental-cart" data-testid="rental-cart-link"
              className="hidden sm:inline-flex items-center gap-2 chip text-xs hover:bg-[#D4FF00] hover:text-black transition">
              <ShoppingBag size={14}/>
              <span className="font-bold">Rental cart</span>
              {cartCount > 0 && (
                <span className="bg-[#FF007F] text-white rounded-full px-1.5 text-[10px] font-black"
                  data-testid="rental-cart-count">{cartCount}</span>
              )}
            </Link>
          )}
        </div>

        {/* Date picker */}
        <div className="mb-3">
          <DateRangePicker
            start={start} end={end} days={days}
            onChange={(s, e) => { setStart(s); setEnd(e); }}/>
        </div>

        {/* Advanced filters */}
        <div className="mb-4">
          <AdvancedFilters
            filters={filters} rentalCats={rentalCats}
            onChange={setFilters}
            onReset={() => setFilters({
              q: "", category_id: "", max_price: "", max_deposit: "",
              min_rating: "", size: "", city: "", sort: "",
            })}/>
        </div>

        {/* Status bar */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Filter size={14} className="text-[#A1A1AA]"/>
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">
            {loading ? "loading…" :
              view === "products"
                ? `${searchResults.length} matching items · for ${days} day${days !== 1 ? "s" : ""}`
                : `${stores.length} ateliers · sorted by rating`}
          </span>
          {user && (
            <Link to="/rental-cart" data-testid="rental-cart-link-mobile"
              className="ml-auto sm:hidden inline-flex items-center gap-1 text-[10px] font-bold text-[#D4FF00]">
              <ShoppingBag size={11}/> Cart ({cartCount})
            </Link>
          )}
        </div>

        {toast && (
          <div className="mb-4 p-3 rounded-xl bg-[#D4FF00]/10 border border-[#D4FF00]/40 text-[#D4FF00] text-sm font-bold"
            data-testid="rental-toast">{toast}</div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-12 text-[#A1A1AA] text-sm">Loading…</div>
        ) : view === "products" ? (
          searchResults.length === 0 ? (
            <div className="card p-10 text-center">
              <Package size={32} className="mx-auto text-[#A1A1AA] opacity-40 mb-2"/>
              <div className="font-display font-bold text-xl uppercase mb-1">No items match</div>
              <div className="text-sm text-[#A1A1AA]">Try widening the filters or clearing the date range.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
              data-testid="rental-search-grid">
              {searchResults.map(p => {
                const saved = savedIds.has(p.product_id);
                return (
                  <div key={p.product_id} className="card overflow-hidden group"
                    data-testid={`rental-result-${p.product_id}`}>
                    <div className="relative aspect-[3/4] overflow-hidden">
                      {p.image && <img src={p.image} alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition"/>}
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSave(p.product_id); }}
                        data-testid={`rental-result-save-${p.product_id}`}
                        className={`absolute top-2 right-2 w-7 h-7 rounded-full backdrop-blur flex items-center justify-center ${
                          saved ? "bg-[#FF007F] text-white" : "bg-black/60 text-white hover:bg-[#FF007F]"
                        }`}>
                        <Heart size={12} className={saved ? "fill-white" : ""}/>
                      </button>
                      <div className="absolute bottom-2 left-2 text-[9px] uppercase tracking-widest font-bold bg-black/70 text-[#D4FF00] px-2 py-0.5 rounded">
                        {fmtMoney(p.price_per_day)}/day
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="font-display font-bold text-sm line-clamp-1">{p.name}</div>
                      <div className="text-[10px] text-[#A1A1AA] line-clamp-1">{p.store_name} · {p.city}</div>
                      {p.description && (
                        <div className="text-[10px] text-[#A1A1AA] mt-1 line-clamp-2">{p.description}</div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-[10px] text-[#A1A1AA]">Dep <span className="text-[#FAFAFA] font-bold">{fmtMoney(p.deposit, { decimals: 0 })}</span></div>
                        <div className="flex items-center gap-1 text-[#D4FF00] text-[10px]">
                          <Star size={10} fill="#D4FF00"/>{p.rating.toFixed(1)}
                        </div>
                      </div>
                      {p.sizes?.length > 0 && (
                        <div className="mt-1 text-[9px] text-[#A1A1AA]">
                          Sizes: <span className="text-[#FAFAFA]">{p.sizes.join(", ")}</span>
                        </div>
                      )}
                      <button onClick={() => quickAddToCart(p)}
                        data-testid={`rental-result-add-${p.product_id}`}
                        className="btn-primary w-full mt-2 text-[10px] inline-flex items-center justify-center gap-1">
                        <Plus size={11}/> Add to cart
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          stores.length === 0 ? (
            <div className="card p-10 text-center">
              <Package size={32} className="mx-auto text-[#A1A1AA] opacity-40 mb-2"/>
              <div className="font-display font-bold text-xl uppercase mb-1">No ateliers in this filter</div>
              <div className="text-sm text-[#A1A1AA]">Try clearing the city filter or switching category.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              data-testid="rental-stores-grid">
              {stores.map(s => (
                <RentalStoreCard key={s.store_id} store={s}
                  onOpen={setDrawerStore}/>
              ))}
            </div>
          )
        )}

        {/* Drawer */}
        {drawerStore && (
          <StoreDrawer store={drawerStore} days={days}
            startDate={start} endDate={end}
            savedIds={savedIds}
            onToggleSave={toggleSave}
            onClose={() => setDrawerStore(null)}
            onAddedToCart={(n) => {
              setToast(`Added ${n} item${n !== 1 ? "s" : ""} to rental cart`);
              setTimeout(() => setToast(""), 2500);
              refreshCart();
            }}
            onReserved={() => {
              setToast("Reservation confirmed! Check My Rentals.");
              setTimeout(() => setToast(""), 3500);
              fetchStores();
            }}/>
        )}
      </div>
    </div>
  );
}
